import { isValid, parseISO } from 'date-fns'
import { isDate } from 'lodash-es'

import { isString } from '@/utils/isString'

export type ActivityChangeKind = 'created' | 'updated'

/**
 * Decides whether an activity is presented as newly created or as updated.
 *
 * There is no event history behind activities, so the only available signal is
 * whether the update timestamp still matches the creation timestamp: equal
 * timestamps mean "created", any difference means "updated". A missing or
 * unreadable timestamp is treated as "created", because that is the weaker
 * statement of the two.
 *
 * @param createdAt Creation timestamp, Date or ISO string.
 * @param updatedAt Last update timestamp, Date or ISO string.
 */
export const getActivityChangeKind = (
  createdAt: unknown,
  updatedAt: unknown,
): ActivityChangeKind => {
  const created = toTimestamp(createdAt)
  const updated = toTimestamp(updatedAt)

  if (created === undefined || updated === undefined) return 'created'

  return updated === created ? 'created' : 'updated'
}

const toTimestamp = (value: unknown): number | undefined => {
  if (isDate(value)) {
    return isValid(value) ? value.getTime() : undefined
  }

  if (isString(value) && value) {
    const parsed = parseISO(value)
    return isValid(parsed) ? parsed.getTime() : undefined
  }

  return undefined
}
